import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'

const passions = [
  {
    id: 'rpg',
    name: 'MMORPG & RPG',
    desc: 'Sei abituato a farmare XP, grindare reputazioni e ottimizzare statistiche per ore.',
    power: 'Grinding & Disciplina Reale',
    value: 85,
    latiPositivi: 'Capacità di grinding (svolgere compiti ripetitivi per obiettivi a lungo termine), eccezionale gestione delle risorse, ottimizzazione delle statistiche (approccio analitico ai dati), attitudine naturale al gioco di squadra (Raid).',
    carriereIdeali: 'Project Manager, Database Administrator, Operations Specialist, Finanza o Data Analyst.',
    color: '#8b5cf6',
  },
  {
    id: 'strategy',
    name: 'Strategia & Scacchi',
    desc: 'Analizzi schemi complessi, gestisci risorse e pianifichi mosse con anticipo.',
    power: 'Pianificazione Critica',
    value: 90,
    latiPositivi: 'Forte pensiero logico-matematico, pianificazione strategica multi-scenario, problem solving avanzato sotto pressione, allocazione ottimale delle risorse attentive.',
    carriereIdeali: 'Software Engineer, Business Strategist, Risk Analyst, Specialista SEO o Marketing Strategico.',
    color: '#00d4ff',
  },
  {
    id: 'anime',
    name: 'Anime & Manga',
    desc: 'Vivi storie di eroi che superano limiti divini dopo sconfitte devastanti.',
    power: 'Zenkai Resilience',
    value: 95,
    latiPositivi: 'Altissima empatia e intelligenza emotiva, resilienza d\'acciaio di fronte ai fallimenti (Zenkai Boost biologico), capacità di sognare in grande, pensiero creativo fuori dagli schemi e attitudine a ispirare gli altri.',
    carriereIdeali: 'Copywriter, UX Designer, Content Creator, HR Specialist, Insegnante o Formatore.',
    color: '#ef4444',
  },
  {
    id: 'dnd',
    name: 'D&D & GdR da Tavolo',
    desc: 'Risolvi problemi in party, interpreti ruoli e ti adatti ad imprevisti colossali.',
    power: 'Adattamento & Carisma',
    value: 80,
    latiPositivi: 'Storytelling persuasivo e carismatico, eccezionali capacità relazionali ed empatia nel gruppo, improvvisazione rapida in contesti di forte incertezza, gestione ottimale dei conflitti e della diversità nel party.',
    carriereIdeali: 'Sales & Account Manager, Public Relations, Creative Marketing Director, Facilitatore o Scrum Master.',
    color: '#facc15',
  },
  {
    id: 'coding',
    name: 'Sci-Fi & Coding',
    desc: 'Costruisci sistemi complessi dal nulla e fai debugging mentale continuo.',
    power: 'Problem Overclock',
    value: 88,
    latiPositivi: 'Profondo pensiero sistemico (capacità di vedere il mondo come insieme di regole interconnesse), debugging metodico dei problemi, spiccata attitudine all\'auto-apprendimento e alla ricerca autonoma.',
    carriereIdeali: 'Full-Stack Developer, AI Engineer, System Architect, Cybersecurity Consultant.',
    color: '#10b981',
  },
]

export default function NerdPowerPage() {
  const [selectedPassions, setSelectedPassions] = useState([])
  const [calibrating, setCalibrating] = useState(false)
  const [calibrated, setCalibrated] = useState(false)

  const togglePassion = (id) => {
    if (selectedPassions.includes(id)) {
      setSelectedPassions(selectedPassions.filter(p => p !== id))
    } else {
      setSelectedPassions([...selectedPassions, id])
    }
    setCalibrated(false)
  }

  const handleCalibrate = () => {
    if (selectedPassions.length === 0) return
    setCalibrating(true)
    setTimeout(() => {
      setCalibrating(false)
      setCalibrated(true)
    }, 1800)
  }

  const getActivePowerScore = () => {
    if (selectedPassions.length === 0) return 0
    const sum = selectedPassions.reduce((acc, id) => {
      const p = passions.find(x => x.id === id)
      return acc + (p ? p.value : 0)
    }, 0)
    return Math.round(sum / selectedPassions.length)
  }

  return (
    <>
      <SEO 
        title="Nerd Power | Sblocca i tuoi Poteri Offline"
        description="Le tue passioni non sono distrazioni. Sono abilità reali non ancora calibrate. Scopri come trasformare gaming, anime e D&D in superpoteri offline."
      />

      <div className="min-h-screen pt-32 pb-24 px-4 relative overflow-hidden" style={{ background: '#050510' }}>
        {/* Background decorations */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(139,92,246,0.08),transparent_65%)] pointer-events-none" />
        <div className="absolute top-20 right-10 w-96 h-96 rounded-full bg-nerd-cyan/5 blur-3xl pointer-events-none" />
        <div className="absolute bottom-20 left-10 w-96 h-96 rounded-full bg-nerd-purple/5 blur-3xl pointer-events-none" />

        <div className="max-w-5xl mx-auto relative z-10 space-y-16">
          {/* Header */}
          <div className="text-center space-y-4">
            <span className="text-[10px] text-nerd-purple font-display uppercase tracking-[0.3em] font-bold">System Log: Nerd Power</span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold text-nerd-white">
              Le tue passioni sono <br />
              <span className="text-gradient">Superpoteri Offline.</span>
            </h1>
            <p className="text-nerd-gray text-base md:text-lg max-w-2xl mx-auto leading-relaxed font-body">
              Non ti chiediamo di rinnegare ciò che ami. Ti insegniamo a ricalibrare le tue abilità virtuali per dominare la vita reale. Ogni ora spesa davanti a uno schermo ha forgiato abilità sinaptiche che oggi puoi sbloccare offline.
            </p>
          </div>

          {/* Interactive Calibrator Section */}
          <div className="manga-panel rounded-2xl p-6 md:p-10 relative overflow-hidden bg-nerd-dark/45 border-nerd-purple/20">
            {/* RPG Corners */}
            <div className="absolute top-3 left-3 w-5 h-5 border-l border-t border-nerd-cyan/30" />
            <div className="absolute top-3 right-3 w-5 h-5 border-r border-t border-nerd-cyan/30" />
            <div className="absolute bottom-3 left-3 w-5 h-5 border-l border-b border-nerd-cyan/30" />
            <div className="absolute bottom-3 right-3 w-5 h-5 border-r border-b border-nerd-cyan/30" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              
              {/* Selector Col */}
              <div className="lg:col-span-6 space-y-6">
                <div>
                  <h3 className="font-display text-lg font-bold text-nerd-white mb-2">1. Seleziona le tue passioni nerd</h3>
                  <p className="text-nerd-gray text-xs font-body">Scegli una o più attività in cui eccelli online per calcolare la tua build offline.</p>
                </div>

                <div className="space-y-3">
                  {passions.map((p) => {
                    const isSelected = selectedPassions.includes(p.id)
                    return (
                      <div
                        key={p.id}
                        onClick={() => togglePassion(p.id)}
                        className="p-4 rounded-xl border cursor-pointer select-none transition-all duration-300 relative group overflow-hidden"
                        style={{
                          borderColor: isSelected ? p.color : 'rgba(139,92,246,0.15)',
                          background: isSelected ? `${p.color}08` : 'rgba(5, 5, 20, 0.4)',
                        }}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="space-y-1">
                            <h4 className="font-display text-sm font-bold text-nerd-white transition-colors" style={{ color: isSelected ? p.color : '#ffffff' }}>
                              {p.name}
                            </h4>
                            <p className="text-[11px] text-nerd-gray font-body leading-relaxed">{p.desc}</p>
                          </div>
                          <div
                            className="w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors"
                            style={{
                              borderColor: isSelected ? p.color : 'rgba(139,92,246,0.3)',
                              background: isSelected ? p.color : 'transparent',
                            }}
                          >
                            {isSelected && (
                              <svg viewBox="0 0 24 24" fill="none" stroke="#050510" strokeWidth="3.5" className="w-3.5 h-3.5">
                                <path d="M5 12l5 5L20 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>

                <button
                  onClick={handleCalibrate}
                  disabled={selectedPassions.length === 0 || calibrating}
                  className="w-full py-3.5 nerd-btn-primary font-display text-xs font-bold uppercase tracking-wider disabled:opacity-40 disabled:pointer-events-none transition-all duration-300"
                >
                  {calibrating ? 'Ricalibrazione Recettori...' : 'Calibra Nerd Power Offline ⚡'}
                </button>
              </div>

              {/* Display Output Col */}
              <div className="lg:col-span-6 flex flex-col justify-center h-full w-full">
                <AnimatePresence mode="wait">
                  {calibrating && (
                    <motion.div
                      key="calibrating"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="manga-panel rounded-xl p-8 text-center space-y-6 bg-nerd-bg border-nerd-cyan/35 shadow-[0_0_30px_rgba(0,212,255,0.1)]"
                    >
                      <div className="w-12 h-12 border-2 border-nerd-cyan border-t-transparent rounded-full animate-spin mx-auto" />
                      <div className="space-y-2">
                        <h4 className="font-display text-sm font-bold text-nerd-white uppercase tracking-widest animate-pulse">Sintonia Attitudinale...</h4>
                        <p className="text-nerd-gray text-xs font-body">Allineamento recettori dopaminergici in corso. Traducendo la tua lore virtuale in XP fisici e sbocchi professionali...</p>
                      </div>
                    </motion.div>
                  )}

                  {calibrated && !calibrating && (
                    <motion.div
                      key="results"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-6 w-full"
                    >
                      <div className="manga-panel rounded-xl p-6 bg-nerd-bg border-nerd-purple/35 relative overflow-hidden">
                        <div className="absolute -inset-10 bg-radial pointer-events-none opacity-20"
                          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)' }}
                        />
                        
                        <div className="text-center space-y-2 relative z-10">
                          <span className="text-[9px] text-nerd-purple font-display uppercase tracking-widest font-bold block">Status: Analisi Completata</span>
                          <h4 className="font-display text-2xl font-bold text-nerd-white">LATO ATTITUINALE OFFLINE</h4>
                          <div className="inline-block px-4 py-1.5 rounded-full bg-nerd-purple/10 border border-nerd-purple/30 text-xs font-display font-bold text-gradient mt-2">
                            Punteggio Focus Reale: {getActivePowerScore()}/100
                          </div>
                        </div>

                        {/* Power Breakdown */}
                        <div className="space-y-5 mt-6 relative z-10">
                          {selectedPassions.map((id) => {
                            const p = passions.find(x => x.id === id)
                            if (!p) return null
                            return (
                              <div key={p.id} className="p-4 rounded-lg bg-nerd-dark/40 border border-nerd-purple/10 space-y-2.5">
                                <div className="flex justify-between text-xs">
                                  <span className="font-display font-bold text-nerd-white flex items-center gap-1.5">
                                    <span style={{ color: p.color }}>◆</span> {p.name} {' → '} {p.power}
                                  </span>
                                  <span className="font-display font-bold" style={{ color: p.color }}>+{p.value}%</span>
                                </div>
                                <div className="h-1 w-full bg-nerd-dark rounded-full overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${p.value}%`, background: p.color }} />
                                </div>
                                
                                <div className="space-y-1.5 pt-1 text-xs leading-relaxed">
                                  <div>
                                    <span className="text-[9px] font-display font-bold uppercase tracking-wider text-nerd-cyan block mb-0.5">Lati Positivi & Soft Skills reali:</span>
                                    <p className="text-nerd-gray text-[11px] font-body">{p.latiPositivi}</p>
                                  </div>
                                  <div>
                                    <span className="text-[9px] font-display font-bold uppercase tracking-wider text-nerd-purple block mb-0.5">Carriere ideali consigliate:</span>
                                    <p className="text-nerd-white text-[11px] font-body font-semibold">{p.carriereIdeali}</p>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>

                      <div className="p-4 rounded-xl border border-nerd-cyan/20 bg-nerd-cyan/5 text-center">
                        <p className="text-nerd-gray text-xs leading-relaxed font-body mb-3">
                          Le tue statistiche sinaptiche indicano un potenziale enorme. Sblocca la tua build e inizia ad accumulare XP reali oggi stesso.
                        </p>
                        <Link to="/#lead-game" className="nerd-btn-primary text-xs inline-block">
                          Attiva il tuo Respawn gratis
                        </Link>
                      </div>
                    </motion.div>
                  )}

                  {!calibrated && !calibrating && (
                    <motion.div
                      key="idle"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="manga-panel rounded-xl p-8 text-center border-dashed border-nerd-purple/20 h-64 flex flex-col items-center justify-center space-y-3 bg-rgba w-full"
                      style={{ background: 'rgba(5, 5, 20, 0.2)' }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="rgba(139,92,246,0.3)" strokeWidth="1.5" className="w-12 h-12 animate-pulse">
                        <polygon points="12,2 2,22 22,22" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                      <div className="space-y-1">
                        <h4 className="font-display text-xs font-bold text-nerd-gray uppercase tracking-widest">In attesa di input...</h4>
                        <p className="text-[10px] text-nerd-gray/50 max-w-xs font-body">Seleziona le tue passioni nella colonna sinistra e clicca su "Calibra" per calcolare i tuoi lati positivi e sbocchi di carriera.</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

            </div>
          </div>

          {/* Theoretical Core Pillars of Nerd Power */}
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-nerd-white">Le 4 Leggi di Conversione del Potere</h2>
              <p className="text-nerd-gray text-xs sm:text-sm font-body max-w-md mx-auto mt-2">Come convertiamo l'energia che investi negli schermi in vittorie reali.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="manga-panel rounded-xl p-6 space-y-3 border-nerd-purple/15 bg-rgba" style={{ background: 'rgba(5, 5, 20, 0.3)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-nerd-purple/10 border border-nerd-purple/30 flex items-center justify-center font-display text-sm font-bold text-nerd-purple">01</div>
                  <h3 className="font-display text-base font-bold text-nerd-white">Hyperfocus Calibrato</h3>
                </div>
                <p className="text-nerd-gray text-xs md:text-sm leading-relaxed font-body">
                  La stessa abilità che ti permette di restare concentrato per 4 ore su un dungeon o un boss di un raid è un'immensa risorsa cognitiva. Impariamo ad applicare questa **attenzione profonda** (Deep Work) allo studio o al lavoro eliminando le notifiche civetta.
                </p>
              </div>

              <div className="manga-panel rounded-xl p-6 space-y-3 border-nerd-cyan/15 bg-rgba" style={{ background: 'rgba(5, 5, 20, 0.3)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-nerd-cyan/10 border border-nerd-cyan/30 flex items-center justify-center font-display text-sm font-bold text-nerd-cyan">02</div>
                  <h3 className="font-display text-base font-bold text-nerd-cyan">Zenkai Boost Reale</h3>
                </div>
                <p className="text-nerd-gray text-xs md:text-sm leading-relaxed font-body">
                  Nei mondi anime (come Dragon Ball), i guerrieri risorgono più forti dopo aver sfiorato la sconfitta. Nella vita reale, riprogrammiamo il tuo dialogo interno per vedere ogni fallimento non come un game over, ma come un **upgrade biologico di resilienza**.
                </p>
              </div>

              <div className="manga-panel rounded-xl p-6 space-y-3 border-nerd-red/15 bg-rgba" style={{ background: 'rgba(5, 5, 20, 0.3)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-nerd-red/10 border border-nerd-red/30 flex items-center justify-center font-display text-sm font-bold text-nerd-red">03</div>
                  <h3 className="font-display text-base font-bold text-nerd-white">Grinding Mindset</h3>
                </div>
                <p className="text-nerd-gray text-xs md:text-sm leading-relaxed font-body">
                  Nessun giocatore si lamenta se deve fare grinding per salire di livello. Capisci che le azioni ripetitive forgiano l'eroe. Applichiamo questa mentalità all'allenamento fisico e alle abitudini quotidiane offline: **XP farming quotidiano**.
                </p>
              </div>

              <div className="manga-panel rounded-xl p-6 space-y-3 border-nerd-gold/15 bg-rgba" style={{ background: 'rgba(5, 5, 20, 0.3)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-nerd-gold/10 border border-nerd-gold/30 flex items-center justify-center font-display text-sm font-bold text-nerd-gold">04</div>
                  <h3 className="font-display text-base font-bold text-nerd-gold">Allineamento dell'Avatar</h3>
                </div>
                <p className="text-nerd-gray text-xs md:text-sm leading-relaxed font-body">
                  Smetti di trascurare il tuo corpo fisico offline. Impariamo a vedere il tuo corpo come l'hardware primario, il tuo avatar reale. Tracciamo sonno, alimentazione e stamina fisica con lo stesso rigore con cui analizzi una scheda personaggio RPG.
                </p>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center py-10 border-t border-nerd-purple/10">
            <h3 className="font-display text-2xl font-bold text-nerd-white mb-4">Sei pronto a ricalibrare la tua build offline?</h3>
            <div className="flex justify-center gap-4">
              <Link to="/#lead-game" className="nerd-btn-primary text-sm inline-block">
                Fai il Test Libro-Game in Homepage
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
